import api from '../config/api';

/**
 * Booking Requests Service
 * Handles all booking request-related API calls
 */

/**
 * Get all booking requests with pagination
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Items per page (default: 10)
 * @param {string} params.search - Search query
 * @param {string} params.status - Filter by status
 * @returns {Promise} API response with booking requests data and pagination
 */
export const getBookingRequests = async (params = {}) => {
  try {
    const { page = 1, limit = 10, search, status } = params;
    
    // Calculate offset for pagination
    const offset = (page - 1) * limit;
    
    // Build query parameters
    const queryParams = {
      limit,
      offset,
    };
    
    if (search) queryParams.search = search;
    if (status) queryParams.status = status;
    
    const response = await api.get('/api/booking-requests', { params: queryParams });
    
    // API returns { success, data: { items, total, limit, offset } }
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to fetch booking requests. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Get a single booking request by ID
 * @param {number|string} id - Booking request ID (numeric)
 * @returns {Promise} API response with booking request data
 */
export const getBookingRequestById = async (id) => {
  try {
    // Ensure id is a number
    const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
    if (isNaN(numericId)) {
      throw new Error('Invalid booking request ID. ID must be a number.');
    }
    
    const response = await api.get(`/api/booking-requests/${numericId}`);
    return response.data || response;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to fetch booking request. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Search for a booking request by request_id (e.g., BR-2025-001) or LR number
 * @param {string} requestIdOrLr - Request ID (BR-XXXX) or LR number (LR-XXXX)
 * @returns {Promise} API response with booking request data
 */
export const searchBookingRequestByRequestId = async (requestIdOrLr) => {
  try {
    if (!requestIdOrLr || !requestIdOrLr.trim()) {
      throw new Error('Request ID or LR number is required.');
    }

    // Search using the search parameter which should match request_id
    const response = await getBookingRequests({
      search: requestIdOrLr.trim(),
      limit: 10,
      page: 1
    });

    // Extract items from response
    const items = response.data?.items || response.items || [];
    
    // Find exact match by request_id (case-insensitive)
    const upperSearch = requestIdOrLr.trim().toUpperCase();
    const matchedRequest = items.find(item => {
      const requestId = (item.request_id || '').toUpperCase();
      return requestId === upperSearch;
    });

    if (matchedRequest) {
      // Fetch full details using numeric ID
      const numericId = matchedRequest.id;
      if (numericId) {
        return await getBookingRequestById(numericId);
      }
    }

    throw new Error('Booking request not found. Please check the request ID or LR number and try again.');
  } catch (error) {
    // If it's already our custom error, re-throw it
    if (error.message.includes('Booking request not found') || error.message.includes('is required')) {
      throw error;
    }
    
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to search booking request. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Create a new booking request with products
 * @param {Object} bookingData - Booking request data
 * @param {number} bookingData.customer_id - Customer ID
 * @param {string} bookingData.name - Customer name
 * @param {string} bookingData.email - Customer email (optional)
 * @param {string} bookingData.phone - Customer phone number
 * @param {string} bookingData.alternate_phone - Alternate phone number (optional)
 * @param {string} bookingData.from_location - Pickup location
 * @param {string} bookingData.to_location - Delivery location
 * @param {string} bookingData.vehicle_type - Vehicle type (optional)
 * @param {string} bookingData.requested_date - Requested date (ISO format, optional)
 * @param {string} bookingData.notes - Special instructions (optional)
 * @param {Array} bookingData.products - Array of product objects
 * @param {string} products[].product_name - Product name
 * @param {string} products[].product_category - Product category
 * @param {string} products[].custom_category - Custom category (if category is 'others')
 * @param {number} products[].length_cm - Length in cm
 * @param {number} products[].height_cm - Height in cm
 * @param {number} products[].width_cm - Width in cm
 * @param {number} products[].weight_kg - Weight in kg
 * @returns {Promise} API response with created booking request data
 */
export const createBookingRequest = async (bookingData) => {
  try {
    const response = await api.post('/api/booking-requests', bookingData);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to create booking request. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Accept a booking request (Agency only)
 * @param {number|string} id - Booking request ID (numeric)
 * @returns {Promise} API response with updated booking request data
 */
export const acceptBookingRequest = async (id) => {
  try {
    // Ensure id is a number
    const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
    if (isNaN(numericId)) {
      throw new Error('Invalid booking request ID. ID must be a number.');
    }
    
    const response = await api.put(`/api/booking-requests/${numericId}/accept`);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to accept booking request. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Reject a booking request (Agency only)
 * @param {number|string} id - Booking request ID (numeric)
 * @returns {Promise} API response with updated booking request data
 */
export const rejectBookingRequest = async (id) => {
  try {
    // Ensure id is a number
    const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
    if (isNaN(numericId)) {
      throw new Error('Invalid booking request ID. ID must be a number.');
    }
    
    const response = await api.put(`/api/booking-requests/${numericId}/reject`);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to reject booking request. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Update a booking request (Admin only - for sharing with agency)
 * @param {number|string} id - Booking request ID
 * @param {Object} updateData - Data to update
 * @param {number} updateData.shared_with_agency_id - Agency ID to share with (null to unshare)
 * @returns {Promise} API response with updated booking request data
 */
export const updateBookingRequest = async (id, updateData) => {
  try {
    const response = await api.put(`/api/booking-requests/${id}`, updateData);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to update booking request. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Share booking request with agency (Admin only)
 * @param {number|string} id - Booking request ID (numeric)
 * @param {number} agency_id - Agency ID to share with
 * @returns {Promise} API response with updated booking request data
 */
export const shareBookingRequest = async (id, agency_id) => {
  try {
    // Ensure id is a number
    const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
    if (isNaN(numericId)) {
      throw new Error('Invalid booking request ID. ID must be a number.');
    }
    
    // Ensure agency_id is a number
    const numericAgencyId = typeof agency_id === 'string' ? parseInt(agency_id, 10) : agency_id;
    if (isNaN(numericAgencyId)) {
      throw new Error('Invalid agency ID. Agency ID must be a number.');
    }
    
    // API expects shared_with_agency_id, not agency_id
    const response = await api.put(`/api/booking-requests/${numericId}/share`, { 
      shared_with_agency_id: numericAgencyId 
    });
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to share booking request. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Update booking request status (Admin only)
 * @param {number|string} id - Booking request ID (numeric)
 * @param {string} status - New status (Pending, Contacted, Confirmed, Cancelled, Rejected)
 * @returns {Promise} API response with updated booking request data
 */
export const updateBookingRequestStatus = async (id, status) => {
  try {
    // Ensure id is a number
    const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
    if (isNaN(numericId)) {
      throw new Error('Invalid booking request ID. ID must be a number.');
    }
    
    const response = await api.put(`/api/booking-requests/${numericId}/status`, { status });
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to update booking request status. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Mark start location as reached (Agency only)
 * @param {number|string} id - Booking request ID (numeric)
 * @returns {Promise} API response with updated booking request data
 */
export const markStartLocationReached = async (id) => {
  try {
    const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
    if (isNaN(numericId)) {
      throw new Error('Invalid booking request ID. ID must be a number.');
    }
    
    const response = await api.put(`/api/booking-requests/${numericId}/tracking/start-reached`);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to mark start location as reached. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Mark start location as departed (Agency only)
 * @param {number|string} id - Booking request ID (numeric)
 * @returns {Promise} API response with updated booking request data
 */
export const markStartLocationDeparted = async (id) => {
  try {
    const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
    if (isNaN(numericId)) {
      throw new Error('Invalid booking request ID. ID must be a number.');
    }
    
    const response = await api.put(`/api/booking-requests/${numericId}/tracking/start-departed`);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to mark start location as departed. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Mark stop as reached (Agency only)
 * @param {number|string} id - Booking request ID (numeric)
 * @param {number} stopIndex - Index of the stop
 * @returns {Promise} API response with updated booking request data
 */
export const markStopReached = async (id, stopIndex) => {
  try {
    const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
    if (isNaN(numericId)) {
      throw new Error('Invalid booking request ID. ID must be a number.');
    }
    
    const response = await api.put(`/api/booking-requests/${numericId}/tracking/stop-reached`, { 
      stop_index: stopIndex 
    });
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to mark stop as reached. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Mark stop as departed (Agency only)
 * @param {number|string} id - Booking request ID (numeric)
 * @param {number} stopIndex - Index of the stop
 * @returns {Promise} API response with updated booking request data
 */
export const markStopDeparted = async (id, stopIndex) => {
  try {
    const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
    if (isNaN(numericId)) {
      throw new Error('Invalid booking request ID. ID must be a number.');
    }
    
    const response = await api.put(`/api/booking-requests/${numericId}/tracking/stop-departed`, { 
      stop_index: stopIndex 
    });
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to mark stop as departed. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Mark destination as delivered (Agency only)
 * @param {number|string} id - Booking request ID (numeric)
 * @returns {Promise} API response with updated booking request data
 */
export const markDestinationDelivered = async (id) => {
  try {
    const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
    if (isNaN(numericId)) {
      throw new Error('Invalid booking request ID. ID must be a number.');
    }
    
    const response = await api.put(`/api/booking-requests/${numericId}/tracking/delivered`);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to mark destination as delivered. Please try again.';
    throw new Error(errorMessage);
  }
};

